import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getData, getcountries } from '../../../services/finance/revenueStatus/revenueServices';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Revenue = () => {
  const [dataList, setDataList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimeoutRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [seriesFilter, setSeriesFilter] = useState('');
  const [subDomainFilter, setSubDomainFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [dropdownData, setDropdownData] = useState({
  countries: []
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

// Debounce Effect
  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1); // reset page only when actual search triggered
    }, 600); // 500ms debounce delay

    return () => clearTimeout(debounceTimeoutRef.current);
  }, [searchText]);

  // Fetch data list
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
          const filters = {};
          if (subDomainFilter) filters.domain = subDomainFilter;
          if (countryFilter) filters.country = countryFilter;
    
          const params = {
            pdf_status: 1,
            page: currentPage,
            limit: rowsPerPage,
            search: debouncedSearch,
            filters,
            startDate: startDate || null,
            endDate: endDate || null,
          };
    
          const response = await getData(params);
          // ✅ Console log the full API response
          console.log('Fetched Data:', response);
          setDataList(response.rows);
          setTotalRows(response.count);
        } catch (error) {
          console.error('Failed to fetch data:', error);
        } finally {
          setLoading(false);
        }
    }, [currentPage, rowsPerPage, debouncedSearch, subDomainFilter, countryFilter, startDate, endDate]);
        
    const fetchDropdownData = useCallback(async () => {
    try {
      const countries = await getcountries(); // assuming it returns array of objects like { code: 'US', name: 'United States' }
      setDropdownData({ countries });
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);


  useEffect(() => {
      fetchDropdownData();
    }, [fetchDropdownData]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);
  

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
  };
  
  const getCurrencySymbol = (currencyCode) => {
  switch (currencyCode?.toUpperCase()) {
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'INR': return '₹';
    case 'GBP': return '£';
    case 'RUB': return '₽';
    case 'THB': return '฿';
    default: return currencyCode || '';
  }
};

  //based country code append country name
    const getCountryName = (code) => {
    const country = dropdownData.countries.find(c => c.code === code);
    return country ? country.name : code;
    };

  const exportToExcel = () => {
    const filtered = dataList.map((row) => ({
      'Series': row.series,
      'Sub Domain':row.sub_domain,
      'Date of Payment': row.date_of_payment,
      'Name': row.billing_full_name,
      'Country': getCountryName(row.billing_countries),
      'Amount': row.reg_amount,
      'Event': row.event_type,
      'Currency': row.currency_type,
      'Payment': row.payment_type,
      'Billing Address': row.billing_address,
    }));

    const ws = XLSX.utils.json_to_sheet(filtered);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Revenue');
    XLSX.writeFile(wb, `Revenue_${startDate || 'all'}_to_${endDate || 'all'}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const tableData = dataList.map((row, index) => [
      index + 1,
      row.series,
      row.sub_domain,
      row.date_of_payment,
      row.billing_full_name,
      getCountryName(row.billing_countries),
      row.reg_amount,
      row.event_type,
      row.currency_type,
      row.payment_type,
      row.billing_address,
    ]);

    doc.autoTable({
      head: [['#', 'Series', 'Date', 'Name', 'Country', 'Amount', 'Event', 'Currency', 'Payment', 'Billing Address']],
      body: tableData,
    });

    doc.save(`Revenue_${startDate || 'all'}_to_${endDate || 'all'}.pdf`);
  };

  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
      width: '60px',
    },
    { name: 'Date Of Payment', selector: (row) => row.date_of_payment, sortable: true },
{
    name: 'Series',
    selector: (row) => {
      // Assuming the `series` field is in format like "2025-11" or "2025-11-15"
      const date = new Date(row.series);
      // Format as "2025-11 Nov 2025"
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-based index, so we add 1
      const months = [

        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'

      ];
      return ` ${months[month]} ${year}`;
    },
    sortable: true,
   },    
   { name: 'Sub Domain', selector: (row) => row.sub_domain, sortable: true },
    { name: 'Name', selector: (row) => row.billing_full_name, sortable: true },
    { name: 'Country', selector: (row) =>  getCountryName(row.billing_countries), sortable: true },
    {
      name: 'Reg Amount',
      selector: (row) => {
        const currencySymbol = getCurrencySymbol(row.currency_type);
        return <b className='fs-4'>{currencySymbol} {row.reg_amount}</b>;
      },
      sortable: true,
    },
    { name: 'Event Type', selector: (row) => row.event_type, sortable: true },
    { name: 'Currency Type', selector: (row) => row.currency_type, sortable: true },
    { name: 'Payment Type', selector: (row) => row.payment_type, sortable: true },
  ];

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        <div className=" row m-3 d-flex flex-column gap-3 ">
          <div className="d-flex gap-2">
            {/* Dropdown for Filters */}
            <div className="col-md-2">
              <span>Series</span>
              <select className="form-control" value={seriesFilter} onChange={(e) => setSeriesFilter(e.target.value)}>
                <option value="">All Series</option>
                {Array.from(new Set(dataList.map(item => item.series))).map((series, idx) => (
                  <option key={idx} value={series}>{series}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span>Sub Domain</span>
              <select className="form-control" value={subDomainFilter} onChange={(e) => setSubDomainFilter(e.target.value)}>
                <option value="">All Subdomains</option>
                {Array.from(new Set(dataList.map(item => item.sub_domain))).map((subDomain, idx) => (
                  <option key={idx} value={subDomain}>{subDomain}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
            <span>Countries</span>
            <select
              className="form-control"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
            >
              <option value="">All Countries</option>
              {dropdownData.countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

            {/* Start Date and End Date Input */}
            <div className="col-md-2">
              <span>Start Date</span>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <span>End Date</span>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={exportToExcel}>Export Excel</button>
            <button className="btn btn-danger" onClick={exportToPDF}>Export PDF</button>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
            <Spinner />
          </div>
        ) : (
          <div> 
            <div className="container-fluid p-4">
              <div className="row mb-3">
                <div className="col">
                  <p className="fs-4">Revenue Status / Revenue</p>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={dataList}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[10, 30, 50, 100, 500]}
                onChangePage={handlePageChange}
                paginationDefaultPage={currentPage}
                onChangeRowsPerPage={handlePerRowsChange}
                highlightOnHover
                responsive
                striped
                subHeader
                subHeaderComponent={
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Search"
                    value={searchText}
                    onChange={handleSearchChange}
                  />
                }
            />
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default Revenue;
